import { useState } from 'react';
import axios from 'axios';
import type { Post } from '../types';
import { PostStatus } from '../types/enums';
import { postsService } from '../services/postsService';
import { toastService } from '../services/toastService';
import { getTomorrow } from '../utils/dateUtils';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { extractApiErrorMessage } from '../utils/apiError';
import { Logger } from '../utils/logger';

export function usePosts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [queue, setQueue] = useState<Post[]>([]);
  const [newsletterPosts, setNewsletterPosts] = useState<Post[]>([]);
  const [droppedPostForScheduling, setDroppedPostForScheduling] = useState<Post | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const { currentSpace } = useAuth();
  const { t } = useLanguage();

  const addToQueue = async (post: Post, scheduledDate?: Date, onScheduled?: (post: Post) => void) => {
    // Snapshot pour annuler l'affichage optimiste si le backend refuse (ex. 422
    // V4-SCHEDULE-NO-VALIDATION : post Instagram sans image).
    const previousPost = posts.find((p) => p.id === post.id);
    const wasQueued = queue.some((p) => p.id === post.id);
    const postWithDate = {
      ...post,
      scheduledDate: scheduledDate || getTomorrow(), // Default: tomorrow
      status: PostStatus.SCHEDULED,
    };

    // Le post reste dans la galerie mais avec une scheduledDate (il sera affiché avec opacity-50)
    // Sauf pour les newsletters qui ne sont pas dans la galerie
    if (!post.isNewsletter) {
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? postWithDate : p))
      );
    }

    setQueue((prev) => {
      const newQueue = [...prev.filter((p) => p.id !== post.id), postWithDate];
      // Sort by scheduled date (earliest on the right)
      return newQueue.sort((a, b) => 
        (b.scheduledDate?.getTime() || 0) - (a.scheduledDate?.getTime() || 0)
      );
    });

    // Call API to persist the scheduled date
    try {
      // For newsletter posts (created client-side with new UUID), we need to CREATE them first
      // For existing posts from the gallery, we UPDATE them
      if (post.isNewsletter) {
        await postsService.create(currentSpace?.id || '', postWithDate);
        Logger.log('Newsletter post created and scheduled successfully in database');
      } else {
        await postsService.update(postWithDate, currentSpace?.id);
        Logger.log('Post scheduled successfully in database');
      }
      // Notify caller (e.g., to update allDatedPosts for calendar)
      onScheduled?.(postWithDate);
    } catch (error) {
      Logger.error('Error scheduling post:', error);
      // Le post n'est PAS programmé côté serveur : on retire l'état optimiste
      // et on affiche la raison renvoyée par le backend.
      if (previousPost) {
        setPosts((prev) => prev.map((p) => (p.id === post.id ? previousPost : p)));
      }
      if (!wasQueued) {
        setQueue((prev) => prev.filter((p) => p.id !== post.id));
      }
      toastService.error(
        t.toasts.scheduleFailed,
        extractApiErrorMessage(error, t.toasts.scheduleFailedDesc),
      );
    }
  };

  const updatePost = async (post: Post, scheduledDate?: Date, onUpdated?: (post: Post) => void) => {
    const postToUpdate = {
      ...post,
      scheduledDate: scheduledDate || post.scheduledDate
    };

    // Optimistic update in gallery
    setPosts((prev) => 
      prev.map((p) => 
        p.id === post.id 
          ? postToUpdate
          : p
      )
    );

    // Optimistic update in queue
    setQueue((prev) => {
      const exists = prev.some(p => p.id === post.id);
      if (!exists) return prev;
      
      const updatedQueue = prev.map((p) => 
        p.id === post.id 
          ? postToUpdate
          : p
      );
      // Re-sort by scheduled date (earliest on the right)
      return updatedQueue.sort((a, b) => 
        (b.scheduledDate?.getTime() || 0) - (a.scheduledDate?.getTime() || 0)
      );
    });
    
    // Call API
    try {
      const updatedPost = await postsService.update(postToUpdate, currentSpace?.id);
      if (updatedPost) {
        Logger.log('Post updated successfully in database');
        onUpdated?.(postToUpdate);
      }
    } catch (error) {
      Logger.error('Error updating post:', error);
    }
  };

  const unschedulePost = async (postId: string) => {
    // After a page refresh, scheduled future posts live only in `queue`, not in `posts`.
    // We must handle both cases.
    const postInPosts = posts.find(p => p.id === postId);
    const postInQueue = queue.find(p => p.id === postId);

    if (postInPosts) {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, scheduledDate: undefined } : p));
    } else if (postInQueue) {
      // Post existed only in queue: restore it to gallery without a scheduled date
      setPosts(prev => [{ ...postInQueue, scheduledDate: undefined }, ...prev]);
    }
    setQueue(prev => prev.filter(p => p.id !== postId));

    try {
      await postsService.unschedule(postId);
      Logger.log('Post unscheduled successfully');
    } catch (error) {
      Logger.error('Error unscheduling post:', error);
    }
  };

  const deletePost = async (postId: string) => {
    // Pour les newsletters, pas besoin de supprimer en base car elles ne sont jamais sauvegardées
    const postToDelete = posts.find(p => p.id === postId);
    if (postToDelete?.isNewsletter) {
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      return;
    }

    // Supprimer d'abord de l'état local pour une réponse immédiate. La galerie
    // contient aussi les posts programmés : les retirer également de la file.
    const queuedToDelete = queue.find(p => p.id === postId);
    setPosts((prev) => prev.filter((p) => p.id !== postId));
    setQueue((prev) => prev.filter((p) => p.id !== postId));

    // Puis supprimer de la base de données
    try {
      await postsService.delete(postId);
      Logger.log(`Post ${postId} supprimé de la base de données`);
    } catch (error) {
      Logger.error('Error calling delete post API:', error);
      // Restaurer le post en cas d'erreur
      if (postToDelete) {
        setPosts((prev) => [postToDelete, ...prev]);
      }
      if (queuedToDelete) {
        setQueue((prev) => [...prev, queuedToDelete]);
      }
    }
  };

  const generateNewPosts = (newPosts: Post[]) => {
    // Ces posts viennent de la base de données, on les ajoute juste localement
    // sans les sauvegarder à nouveau (pour éviter les conflits de clés)
    setPosts((prev) => {
      // Filter out posts that already exist (by ID) to avoid duplicates
      const existingIds = new Set(prev.map(p => p.id));
      const uniqueNewPosts = newPosts.filter(p => !existingIds.has(p.id));
      Logger.log(`Ajout de ${uniqueNewPosts.length} nouveaux posts à la galerie (${newPosts.length - uniqueNewPosts.length} déjà présents)`);
      return [...uniqueNewPosts, ...prev];
    });
    setIsGenerateModalOpen(false);
  };

  const createPost = async (post: Post) => {
    if (!currentSpace) return;

    // Cette fonction est utilisée pour créer un NOUVEAU post (pas pour charger depuis la base)
    
    // Vérifier que l'ID est un UUID valide, sinon en générer un nouveau
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let validPost = post;
    
    if (!uuidRegex.test(post.id)) {
      Logger.warn(`Invalid UUID detected: ${post.id}, generating new UUID`);
      validPost = {
        ...post,
        id: crypto.randomUUID()
      };
    }
    
    // Vérifier si le post existe déjà localement AVANT toute opération
    const existsLocally = posts.some(p => p.id === validPost.id);
    if (existsLocally) {
      Logger.log('Post already exists locally, skipping creation');
      return;
    }
    
    // Ajouter localement d'abord
    setPosts((prev) => {
      const exists = prev.some(p => p.id === validPost.id);
      if (exists) {
        Logger.log('Post already exists locally (race condition check), skipping local add');
        return prev;
      }
      return [validPost, ...prev];
    });
    
    // Sauvegarder en BDD Supabase
    try {
      const createdPost = await postsService.create(currentSpace.id, validPost);
      Logger.log('Post created successfully:', createdPost);
    } catch (error: unknown) {
      Logger.error('Error creating post:', error);
      // V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — Si c'est une erreur de duplicata
      // (code 23505 Postgres unique violation), ignorer silencieusement.
      let isDuplicate = false;
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { code?: unknown } | undefined;
        isDuplicate = data?.code === '23505';
      } else if (error && typeof error === 'object' && 'code' in error) {
        isDuplicate = (error as { code?: unknown }).code === '23505';
      }
      if (isDuplicate) {
        Logger.log('Post already exists in database (duplicate key), ignoring error');
      } else {
        // Pour les autres erreurs, retirer le post de la liste locale
        setPosts((prev) => prev.filter((p) => p.id !== validPost.id));
      }
    }
  };

  const handleCreatePost = async (post: Post) => {
    // Ajouter le post à la galerie locale
    setPosts((prev) => [post, ...prev]);
    Logger.log('Post créé:', post);
    // Persister en base
    await createPost(post);
  };

  const duplicatePost = async (post: Post) => {
    // Créer un nouveau post avec un nouvel UUID
    const newPost: Post = {
      ...post,
      id: crypto.randomUUID(), // Générer un nouvel UUID
      generatedDate: new Date(), // Nouvelle date de génération
      scheduledDate: undefined, // Pas de date planifiée pour le duplicata
    };

    // Ajouter à la galerie et sauvegarder en base
    try {
      await createPost(newPost);
      // Fix backlog 3.3 (19-05) : feedback immédiat à l'utilisateur après duplication.
      toastService.success('Post dupliqué');
    } catch (e) {
      Logger.error('Failed to duplicate post:', e);
      toastService.error('Impossible de dupliquer le post');
      throw e;
    }
  };

  // Newsletter functions
  const addToNewsletter = (post: Post) => {
    // Add to newsletter list if not already there
    setNewsletterPosts((prev) => {
      if (prev.some(p => p.id === post.id)) {
        return prev; // Already in newsletter
      }
      return [...prev, post];
    });
  };

  const removeFromNewsletter = (post: Post) => {
    setNewsletterPosts((prev) => prev.filter(p => p.id !== post.id));
  };

  const scheduleNewsletter = (scheduledDate: Date, introduction: string, posts: Post[]) => {
    // Create a newsletter "post" object
    // Newsletter is NOT a communication channel - it's a special post type
    Logger.log('scheduleNewsletter called with:', { scheduledDate, introduction, posts });

    const newsletterPost: Post = {
      id: crypto.randomUUID(),
      content: introduction || 'Newsletter',
      generatedDate: new Date(),
      scheduledDate: scheduledDate,
      channelId: 'newsletter-type', // Special ID for newsletter posts (not a real channel)
      isNewsletter: true, // Special flag to mark this as a newsletter
      newsletterData: {
        introduction,
        posts: posts.map(p => p.id)
      }
    };

    Logger.log('Created newsletter post:', newsletterPost);

    // Add to queue
    addToQueue(newsletterPost, scheduledDate);

    // Clear newsletter posts
    setNewsletterPosts([]);
  };

  const publishPostNow = async (postId: string): Promise<Post | null> => {
    try {
      const publishedPost = await postsService.publishNow(postId);
      // Update local state with published post
      setPosts((prev) => prev.map((p) => p.id === postId ? publishedPost : p));
      setQueue((prev) => prev.filter((p) => p.id !== postId));
      Logger.log('Post published successfully:', publishedPost);
      return publishedPost;
    } catch (error: unknown) {
      Logger.error('Error publishing post:', error);
      // V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — detail peut être string OU object
      // FastAPI ({ message, code, ... }). On gère les deux sans `any`.
      let errorMessage: string = 'Publication failed';
      if (axios.isAxiosError(error)) {
        const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
        if (typeof detail === 'string' && detail) {
          errorMessage = detail;
        } else if (detail && typeof detail === 'object') {
          const detailObj = detail as { message?: unknown };
          if (typeof detailObj.message === 'string' && detailObj.message) {
            errorMessage = detailObj.message;
          } else {
            errorMessage = JSON.stringify(detail);
          }
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }
      setPosts((prev) => prev.map((p) =>
        p.id === postId ? { ...p, errorMessage, status: PostStatus.FAILED } : p
      ));
      return null;
    }
  };

  return {
    posts,
    setPosts,
    queue,
    setQueue,
    newsletterPosts,
    setNewsletterPosts,
    droppedPostForScheduling,
    setDroppedPostForScheduling,
    isGenerateModalOpen,
    setIsGenerateModalOpen,
    addToQueue,
    updatePost,
    unschedulePost,
    deletePost,
    generateNewPosts,
    createPost,
    handleCreatePost,
    duplicatePost,
    addToNewsletter,
    removeFromNewsletter,
    scheduleNewsletter,
    publishPostNow
  };
}
